import { useEffect, useState } from 'react';

import {
  ChatUser,
  DateFormat,
  getOutOfOfficeDateStatus,
} from '@equip.health/ui';
import { Member } from '@sendbird/chat/groupChannel';
import { DateTime } from 'luxon';

import { UserRoles } from '~/lib/constants/chat.constants';
import { getMembersOfRole } from '~/lib/util/chat/chatChannel.util';
import {
  ProviderOutOfOfficeResponse,
  useGetProviderOutOfOfficeDetailsQuery,
} from '~/store/services/providers';

export type OutOfOfficeState = {
  isLoading?: boolean;
  isOutOfOfficeFetched?: boolean;
  chatUserOOODetails: ChatUserOOO[];
};

export type ChatUserOOO = ChatUser & {
  ooo: ProviderOutOfOfficeResponse;
};

const useChatProviderOutOfOffice = (
  channelMembers: Member[],
): OutOfOfficeState => {
  const [chatUserOOODetails, setChatUserOOODetails] = useState<ChatUserOOO[]>();
  const providerMembers = getMembersOfRole(channelMembers, UserRoles.PROVIDER);
  const providerMemberIds = providerMembers.map((m) => m.userId);
  const {
    data: providerMemberOOODetails,
    isUninitialized,
    isFetching,
  } = useGetProviderOutOfOfficeDetailsQuery({
    fromStartDate: DateTime.now().toFormat(DateFormat.shortYearMonthDay),
    providerExternalIds: providerMemberIds,
  });

  const isLoading = isUninitialized || isFetching;

  useEffect(() => {
    if (!isLoading && providerMemberOOODetails && !chatUserOOODetails) {
      const chatUserOOOList: ChatUserOOO[] = [];
      providerMembers.forEach((chatMember) => {
        const activeMemberOOO = providerMemberOOODetails.filter(
          (ooo) =>
            ooo.providerExternalId === chatMember.userId &&
            getOutOfOfficeDateStatus(ooo.startDate, ooo.endDate) === 'active',
        );
        if (activeMemberOOO.length > 0) {
          chatUserOOOList.push({ ...chatMember, ooo: activeMemberOOO[0] });
        }
      });
      if (chatUserOOOList.length > 0) {
        setChatUserOOODetails(chatUserOOOList);
      }
    }
    if (providerMemberOOODetails && providerMemberOOODetails.length === 0) {
      setChatUserOOODetails(undefined);
    }
  }, [
    isLoading,
    providerMemberOOODetails,
    providerMembers,
    chatUserOOODetails,
  ]);

  return {
    chatUserOOODetails,
    isLoading,
    isOutOfOfficeFetched:
      !isLoading && chatUserOOODetails && chatUserOOODetails.length > 0,
  };
};

export default useChatProviderOutOfOffice;
